import type { ProductAvailability, SellingPlanDepositData, ShopifyResponse } from '../Cart/types';
import type { CatalogProduct, CatalogProductOption } from './catalogTypes';

interface NormalizeCatalogResult {
  catalog: Record<string, CatalogProduct>;
  productOptionById: Record<string, { productKey: string; option: CatalogProductOption }>;
}

/**
 * Pure function that normalizes a raw Shopify Storefront API response into
 * the canonical CatalogProduct domain model.
 *
 * This is the single normalization boundary: Shopify's `handle` becomes
 * `productKey`, `variants` become `options` (CatalogProductOption[]), and
 * selling plan data is consolidated into a single nested object.
 */
export const normalizeCatalog = (
  shopifyData: ShopifyResponse | undefined,
  calculateAtp: (availability: ProductAvailability) => string | null
): NormalizeCatalogResult => {
  const catalog: Record<string, CatalogProduct> = {};
  const productOptionById: Record<string, { productKey: string; option: CatalogProductOption }> =
    {};

  if (!shopifyData) {
    return { catalog, productOptionById };
  }

  for (const { node } of shopifyData.products.edges) {
    // productKey = Shopify's "handle" — the stable, human-readable product slug
    const productKey = node.handle;

    // Convert Shopify variants → CatalogProductOption[]
    const options: CatalogProductOption[] = node.variants.edges.map(({ node: variant }) => ({
      id: variant.id,
      title: variant.title,
      sku: variant.sku,
      price: Number(variant.price.amount ?? '0'),
      available: variant.availableForSale,
      options: variant.selectedOptions.reduce((opts, { name, value }) => {
        opts[name] = value;
        return opts;
      }, {} as Record<string, string>),
      compareAtPrice: variant.compareAtPrice ?? null,
      currencyCode: variant.price.currencyCode,
      quantityAvailable: variant.quantityAvailable ?? null,
      quantityRule: variant.quantityRule,
    }));

    // Extract first selling plan ID for checkout URL usage.
    // Assumption: products have at most one selling plan group that matters.
    const sellingPlanGroups = (node.sellingPlanGroups?.edges ?? []).map(({ node: group }) => ({
      name: group.name,
      sellingPlans: group.sellingPlans.edges.map(({ node: plan }) => ({
        id: plan.id,
        name: plan.name,
        description: plan.description ?? undefined,
      })),
    }));
    const firstSellingPlanId = sellingPlanGroups[0]?.sellingPlans[0]?.id;

    // Parse product-level metafields for deposit data and max quantity.
    const sellingPlanDataRaw = node.metafields?.find(
      field => field?.namespace === 'custom' && field?.key === 'sellingplandata'
    );
    const productMaxQuantityRaw = node.metafields?.find(
      field => field?.namespace === 'custom' && field?.key === 'maxquantity'
    );

    let depositAmounts: SellingPlanDepositData | undefined;

    if (sellingPlanDataRaw?.value) {
      try {
        const parsed = JSON.parse(sellingPlanDataRaw.value);

        // The metafield value may be wrapped: { "sellingplandata": { "usd": 25.00, ... } }
        // or may be the deposit map directly: { "usd": 25.00, "eur": 30.00, ... }
        let rawDeposits: Record<string, number> | undefined =
          parsed?.sellingplandata ?? parsed?.sellingPlanData ?? parsed?.SellingPlanData;

        // Fallback: if none of the known wrapper keys matched, check if the parsed
        // value itself looks like a deposit map (keys are currency codes → numbers).
        if (
          !rawDeposits &&
          typeof parsed === 'object' &&
          parsed !== null &&
          !Array.isArray(parsed)
        ) {
          const values = Object.values(parsed);

          if (values.length > 0 && values.every(val => typeof val === 'number')) {
            rawDeposits = parsed as SellingPlanDepositData;
          }
        }

        // Normalize keys to uppercase (ISO 4217) so lookups by currencyCode ("USD")
        // work regardless of how the metafield was authored ("usd", "Usd", "USD").
        if (rawDeposits) {
          depositAmounts = Object.fromEntries(
            Object.entries(rawDeposits).map(([currency, amount]) => [
              currency.toUpperCase(),
              amount,
            ])
          );
        }
      } catch {
        // Invalid JSON in metafield — skip
      }
    }

    const maxQuantity = productMaxQuantityRaw?.value
      ? Number.parseInt(productMaxQuantityRaw.value)
      : undefined;

    // Compute ATP message from first variant metafield
    const firstVariant = node.variants.edges[0];
    const atpDays = firstVariant?.node.metafields?.find(field => field?.key === 'ATPBufferDays');
    const variantMaxQuantity = firstVariant?.node.metafields?.find(
      field => field?.key === 'maxQuantity'
    );

    let atpMessage: string | null = null;

    if (atpDays) {
      atpMessage = calculateAtp({
        available: node.availableForSale,
        atp: Number.parseInt(atpDays.value),
      });
    }

    // Effective max quantity: product-level metafield preferred, variant-level as fallback
    const effectiveMaxQuantity =
      maxQuantity ?? (variantMaxQuantity ? Number.parseInt(variantMaxQuantity.value) : undefined);

    const product: CatalogProduct = {
      productKey,
      nodeId: node.id,
      title: node.title,
      featuredImage: node.featuredImage?.url,
      images: node.images?.edges.map(edge => edge.node) ?? [],
      available: node.availableForSale,
      options,
      maxQuantity: effectiveMaxQuantity,
      currencyCode: node.priceRange.maxVariantPrice.currencyCode,
      atpMessage,
      ...(firstSellingPlanId
        ? {
            sellingPlan: {
              id: firstSellingPlanId,
              depositAmounts,
              requiresPlan: node.requiresSellingPlan ?? false,
              groups: sellingPlanGroups,
            },
          }
        : {}),
    };

    catalog[productKey] = product;

    // Build productOptionById reverse lookup (optionId → productKey + option)
    for (const option of options) {
      productOptionById[option.id] = { productKey, option };
    }
  }

  return { catalog, productOptionById };
};
